import t from 'react-translate';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { MentoringProgramMatchingType } from 'redux/schemas/models/mentoring-programs';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import MatchingTypeCard from './matching-type-card';
import { css } from '@emotion/react';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import { useFormContext } from 'react-hook-form';
import { MATCHING_TYPE_COLORS } from '../constants';
import { useAppDispatch } from 'redux/store';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';

type MatchingTypeSelectorProps = {
  onSubmit: () => void;
};

const styles = css`
  .matching-types-list {
    gap: ${standardSpacing}px;
    li {
      flex: 1;
    }
  }
`;

const MatchingTypeSelector = ({ onSubmit }: MatchingTypeSelectorProps) => {
  const dispatch = useAppDispatch();
  const { setValue } = useFormContext();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id, matchingType, releasedDate } = mentoringProgram || {};
  const isProgramReleased =
    releasedDate && moment().isAfter(moment(releasedDate));
  const aliases = useSelector((state) => getMentoringProgramAliases(state, id));
  const translationBase =
    t.MENTORING_PROGRAMS.SETTINGS.TABS.MATCHING_SETTINGS.MATCHING_TYPE;
  const matchingTypes = {
    [MentoringProgramMatchingType.PARTICIPANT_DRIVEN]: {
      id: MentoringProgramMatchingType.PARTICIPANT_DRIVEN,
      icon: 'meet-your-peers',
      iconBackgroundColor:
        MATCHING_TYPE_COLORS[MentoringProgramMatchingType.PARTICIPANT_DRIVEN],
      title: translationBase.OPTIONS.PARTICIPANT_DRIVEN.TITLE(),
      description:
        translationBase.OPTIONS.PARTICIPANT_DRIVEN.DESCRIPTION(aliases),
      content: translationBase.OPTIONS.PARTICIPANT_DRIVEN.CONTENT(aliases),
      switchConfirmation: {
        title:
          translationBase.OPTIONS.PARTICIPANT_DRIVEN.SWITCH_CONFIRMATION.TITLE(),
        body: translationBase.OPTIONS.PARTICIPANT_DRIVEN.SWITCH_CONFIRMATION.BODY(),
      },
    },
    [MentoringProgramMatchingType.AUTOMATED_MATCHING]: {
      id: MentoringProgramMatchingType.AUTOMATED_MATCHING,
      icon: 'auto-connection',
      iconBackgroundColor:
        MATCHING_TYPE_COLORS[MentoringProgramMatchingType.AUTOMATED_MATCHING],
      title: translationBase.OPTIONS.ASSIGNED.TITLE(),
      description: translationBase.OPTIONS.ASSIGNED.DESCRIPTION(),
      content: translationBase.OPTIONS.ASSIGNED.CONTENT(aliases),
      switchConfirmation: {
        title: translationBase.OPTIONS.ASSIGNED.SWITCH_CONFIRMATION.TITLE(),
        body: translationBase.OPTIONS.ASSIGNED.SWITCH_CONFIRMATION.BODY(),
      },
    },
  };

  const onConfirm = (typeId: MentoringProgramMatchingType) => {
    setValue('matchingType', typeId);
    onSubmit();
  };

  const onSelectType = (typeId: MentoringProgramMatchingType) => {
    if (matchingType !== typeId) {
      if (isProgramReleased) {
        const selectedMatchingType = matchingTypes[typeId];
        dispatch(
          openConfirmationDialog({
            title: selectedMatchingType.switchConfirmation.title,
            bodyText: selectedMatchingType.switchConfirmation.body,
            confirmText: t.FORM.UPDATE(),
            cancelText: t.FORM.CANCEL(),
            onConfirm: () => onConfirm(typeId),
          })
        );
      } else {
        onConfirm(typeId);
      }
    }
  };

  return (
    <div css={styles}>
      <div className='heading-4'>{translationBase.TITLE()}</div>
      <div className='text-regular black mt-2'>
        {translationBase.DESCRIPTION()}
      </div>
      <ul role='listbox' className='d-flex matching-types-list list-unstyled mt-2 mb-4'>
        {Object.values(matchingTypes).map((typeConfig) => (
          <li key={typeConfig.id}>
            <MatchingTypeCard
              {...typeConfig}
              isSelected={matchingType === typeConfig.id}
              onSelect={() => onSelectType(typeConfig.id)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MatchingTypeSelector;
